import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { motion } from "motion/react";
import { MapPin } from "lucide-react";
import { FaUsers, FaHandshake, FaClock, FaLock, FaTools } from "react-icons/fa";

import { useShovellerSignupContext } from "../../../../context/shovllerSignupFormContext";
import { Loader } from "../../../base/Loader";
import { Card, CardContent } from "../../../ui/card";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { StepFooterNav } from "../../shared/NavigationFooter";

const libraries = ["places"];

function debounce(func, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

const FEATURES = [
  {
    title: "Everyone is Welcome",
    description:
      "If you're 14 or older, have a bank account, and want to help your neighbours, you can join!",
    icon: <FaUsers className="w-8 h-6 object-contain text-primary" />,
  },
  {
    title: "Join the Community",
    description:
      "Help your neighbours shovel their driveway or mow their lawn and earn extra income with Shovel House.",
    icon: <FaHandshake className="w-8 h-6 text-primary" />,
  },
  {
    title: "Earn on Your Schedule",
    description:
      "Work when and where you want. Accept jobs that fit your availability.",
    icon: <FaClock className="w-8 h-6 text-primary" />,
  },
  {
    title: "Secure Payments",
    description: "Get paid directly through Stripe, a trusted payment platform.",
    icon: <FaLock className="w-8 h-6 text-primary" />,
  },
  {
    title: "No Tools Needed",
    description: "Homeowners provide the tools. Just show up and work.",
    icon: <FaTools className="w-8 h-6 text-primary" />,
  },
];

export function Step1({ nextStep, preStep, currentStep, totalSteps }) {
  const navigate = useNavigate();
  const { formData, handleChange, setFormData } = useShovellerSignupContext();
  const [autocomplete, setAutocomplete] = useState(null);
  const [errors, setErrors] = useState({});
  const [isAutoFilled, setIsAutoFilled] = useState(false);

  const googleMapsApiKey = "AIzaSyDJQNAbTK3AGLlmRGVxa3VbejegSp-qB9A";

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries,
  });

  // Auto-detect location silently on load
  useEffect(() => {
    if (isLoaded && navigator.geolocation && !isAutoFilled) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          
          // Use Google's reverse geocoding to get address
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            if (status === "OK" && results[0]) {
              const address = results[0].formatted_address;
              
              // Update form data silently
              setFormData((prevData) => ({
                ...prevData,
                latitude: latitude,
                longitude: longitude,
                neighborhood: address,
              }));
              
              setIsAutoFilled(true);
              setErrors((prev) => ({ ...prev, location: null }));
            }
          });
        },
        () => {
          // Silently fail - user can enter manually
        }
      );
    }
  }, [isLoaded, isAutoFilled, setFormData]);

  const onAutocompleteLoad = (instance) => {
    setAutocomplete(instance);
  };

  const handlePlaceSelect = useCallback(
    debounce(() => {
      if (autocomplete) {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const address = place.formatted_address || "";

          setFormData((prevData) => ({
            ...prevData,
            latitude: lat,
            longitude: lng,
            neighborhood: address,
          }));

          setErrors((prev) => ({ ...prev, location: null }));
        }
      }
    }, 500),
    [autocomplete, setFormData]
  );

  // Handle manual text changes - keep coordinates if already set
  const handleNeighborhoodChange = (e) => {
    const value = e.target.value;
    
    // Update the text field but keep coordinates if we already have them
    setFormData((prevData) => ({
      ...prevData,
      neighborhood: value,
      // Don't clear coordinates - keep them unless this is a completely new selection
    }));
    
    // Only set error if field is completely empty and we don't have coordinates
    if (value === "" && (!formData.latitude || !formData.longitude)) {
      setErrors((prev) => ({
        ...prev,
        location: "Please select a valid location.",
      }));
    } else {
      setErrors((prev) => ({ ...prev, location: null }));
    }
  };

  const handleNext = () => {
    const newErrors = {};
    if (!formData.latitude || !formData.longitude) {
      newErrors.location = "Please select a valid location.";
    }
    if (Object.keys(newErrors).length === 0) {
      nextStep();
    } else {
      setErrors(newErrors);
    }
  };

  if (loadError) {
    return <div>Error loading Google Maps</div>;
  }

  if (!isLoaded) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen py-6 lg:py-12">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-6">
          <h1 className="text-2xl sm:text-4xl font-bold">Neighbourhood Helper</h1>

          {/* Features Grid (flex-wrap so incomplete rows center) */}
          <div className="flex flex-wrap justify-center gap-4 text-center md:text-start">
            {FEATURES.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="w-full sm:w-[48%] lg:w-[31%] max-w-sm"
              >
                <Card className="bg-card/60 h-full">
                  <CardContent className="px-5 py-6 flex flex-col gap-3 items-start">
                    <div className="flex items-center gap-3">
                      {feature.icon}
                      <h2 className="font-semibold text-lg">{feature.title}</h2>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Address + Referral in one Card */}
        <Card>
          <CardContent className="space-y-6 pt-6">
            {/* Neighbourhood */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-lg font-semibold">
                <MapPin className="w-5 h-5" /> Enter your neighbourhood or address
              </Label>
              <Autocomplete 
                onLoad={onAutocompleteLoad} 
                onPlaceChanged={handlePlaceSelect}
                options={{
                  types: ['geocode', 'establishment'],
                  componentRestrictions: { country: "ca" },
                }}
              >
                <Input
                  type="text"
                  name="neighborhood"
                  placeholder="e.g., Downtown, Brooklyn"
                  value={formData.neighborhood || ""}
                  onChange={handleNeighborhoodChange}
                  className="w-full py-6 bg-gray-100"
                />
              </Autocomplete>
              {errors.location && (
                <p className="text-red-500 text-sm">{errors.location}</p>
              )}
              <p className="text-xs text-muted-foreground">
                (This is usually near your home address. It helps us find homeowners in your
                vicinity)
              </p>
            </div>

            {/* Referral Code */}
            <div className="space-y-2">
              <Label className="text-lg font-semibold">Referral code (optional)</Label>
              <Input
                type="text"
                name="referredBy"
                className="py-6 bg-gray-100"
                value={formData.referredBy}
                onChange={handleChange}
                placeholder="e.g., 13223"
              />
            </div>
          </CardContent>
        </Card>

        {/* Footer Navigation */}
        <StepFooterNav
          backDisabledOnFirstStep={false}
          currentStep={currentStep}
          totalSteps={totalSteps}
          onBack={(() => navigate("/"))}
          onNext={handleNext}
          nextDisabled={!formData.latitude || !formData.longitude}
        />
      </div>
    </div>
  );
}